import { Build, Component, Element, Prop, State, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

type Column = { label: string; highlighted?: boolean; logoSrc?: string };
/**
 * A cell value that shows extra context in a hover tooltip.
 * `text` is the visible label; the first occurrence of `trigger` inside it is bolded as the
 * tooltip anchor (if `trigger` is omitted, the whole label is the anchor). `tooltip` is the
 * dark popover text shown on hover/focus.
 */
type TooltipValue = { text: string; trigger?: string; tooltip: string };
type CellValue = boolean | string | TooltipValue;
type Row = { feature: string; values: Array<CellValue> };
type PriceValue = { amount: string; currency?: string; period?: string; link?: { label: string; href: string } };
type PriceRow = { feature: string; values: Array<PriceValue> };

const COMPONENT_NAME = 'sn-comparison-type-2';

/**
 * Competitor comparison table section for the SignNow brand.
 * Displays a full-width HTML table comparing SignNow features against up to four competitors,
 * with blue checkmarks for available features and red-tinted X marks for unavailable ones.
 * The promoted (SignNow) column is visually highlighted with a light blue background.
 * A "Show more / Show less" toggle reveals additional rows beyond the initial threshold.
 *
 * @product signnow
 * @version 1.0.0
 * @category SignNow
 * @slot heading - {required}, <me-heading variant="2" tag="h2"> - Section heading above the table
 * @slot subtitle - <me-text variant="large"> - Subtitle beneath the heading
 * @slot disclaimer - <me-text> - Small disclaimer text rendered inside the first header cell
 * @example
 *   <sn-comparison-type-2
 *     columns='[{"label":"airSlate SignNow","highlighted":true},{"label":"DocuSign"},{"label":"Signmee"}]'
 *     rows='[{"feature":"eSign a document","values":[true,true,true]},{"feature":"Kiosk Mode","values":[true,false,false]}]'
 *     initial-visible-rows="10"
 *     show-more-label="Show more"
 *     show-less-label="Show less"
 *     show-more-teaser="Want to see more features?"
 *   >
 *     <me-heading slot="heading" variant="2" tag="h2">The process of electronic signing is faster and more secure than ever before</me-heading>
 *     <me-text slot="subtitle" variant="large">Discover how quick and easy eSigning and data collection can be.</me-text>
 *     <me-text slot="disclaimer">This chart represents a partial list of features available in signNow, DocuSign and Signmee.</me-text>
 *   </sn-comparison-type-2>
 */
@Component({
  tag: 'sn-comparison-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnComparisonType2 {
  @Element() el: HTMLElement;
  /**
   * Table column definitions for the data columns (not the feature-name column).
   * At least two columns are required. Mark the promoted product column with `highlighted: true`.
   * Provide `logoSrc` to render an image logo in the header instead of plain text.
   * @type {array}
   * @col.type {object}
   * @col.property {string} label - Column header text (used as alt text when logoSrc is set)
   * @col.property {boolean} [highlighted] - Apply brand highlight background to this column (optional)
   * @col.property {string} [logoSrc] - URL of a logo image to show in the header cell (optional)
   * @col.required label
   * @minItems 2
   * @maxItems 4
   */
  @Prop({ mutable: true }) columns!: Array<Column> | string;

  /**
   * Comparison rows. `values` entries are aligned to `columns` by index.
   * Use `true` for a checkmark, `false` for an X mark, or a `string` for literal text.
   * @type {array}
   * @row.type {object}
   * @row.property {string} feature - Feature name shown in the first column
   * @row.property {array} values - Per-column availability: true (checkmark), false (cross), a string label, or a tooltip object {"text","trigger","tooltip"} that bolds `trigger` inside `text` and shows `tooltip` on hover
   * @row.required feature
   * @row.required values
   * @row.content feature values
   * @minItems 1
   */
  @Prop({ mutable: true }) rows!: Array<Row> | string;

  /**
   * Number of rows shown before the "Show more" button appears.
   * Set to `0` to display all rows without the toggle.
   * @default 10
   */
  @Prop() initialVisibleRows: number = 15;

  /**
   * Label for the expand toggle.
   * @content
   * @default 'Show more'
   */
  @Prop() showMoreLabel: string = 'Show more';

  /**
   * Label for the collapse toggle.
   * @content
   * @default 'Show less'
   */
  @Prop() showLessLabel: string = 'Show less';

  /**
   * Optional teaser text displayed next to the toggle button (e.g. "Want to see more features?").
   * Hidden when all rows are already visible.
   * @content
   */
  @Prop() showMoreTeaser: string = '';

  /**
   * Where price rows are rendered relative to the feature rows.
   * `'top'` places them before the first feature row; `'bottom'` places them after the last.
   * @default 'bottom'
   */
  @Prop() priceRowsPosition: 'top' | 'bottom' = 'bottom';

  /**
   * Pricing rows. Always visible (not affected by the show-more toggle).
   * Each entry shows a plan name in the feature column and a price block per competitor column.
   * Use `priceRowsPosition` to control whether they appear above or below the feature rows.
   * @type {array}
   * @priceRow.type {object}
   * @priceRow.property {string} feature - Plan tier label shown in the feature column (e.g. "Basic plan")
   * @priceRow.property {array} values - Per-column pricing data aligned to `columns` by index
   * @priceRow.required feature
   * @priceRow.required values
   * @priceRow.content feature
   */
  @Prop({ mutable: true }) priceRows?: Array<PriceRow> | string;

  @State() private expanded: boolean = false;
  @State() private isHeaderStuck: boolean = false;

  private sentinelObserver?: IntersectionObserver;

  componentWillLoad() {
    this.columns = parseJsonProp(this.columns) as Array<Column>;
    this.rows = this.normalizeRows(parseJsonProp(this.rows));
    if (this.priceRows !== undefined) {
      this.priceRows = this.normalizePriceRows(parseJsonProp(this.priceRows));
    }
  }

  /**
   * Coerce a single cell value. The builder can emit availability as the strings
   * "true"/"false" instead of JSON booleans — turn those into booleans so they render
   * as check/cross marks. Real text labels and tooltip objects are left untouched.
   */
  private coerceCellValue(value: unknown): CellValue {
    if (typeof value === 'string') {
      const t = value.trim();
      if (t.toLowerCase() === 'true') return true;
      if (t.toLowerCase() === 'false') return false;
      return t;
    }
    return value as CellValue;
  }

  /**
   * Normalize a row's `values` into an array. The builder sometimes serializes it as
   * a plain string (e.g. "true, true") instead of an array; indexing that string by
   * column then yielded one character per column ("t", "r", …). Parse JSON when
   * possible, otherwise fall back to comma-separated tokens, then coerce each token.
   */
  private normalizeValues(values: unknown): Array<CellValue> {
    if (Array.isArray(values)) return values.map(v => this.coerceCellValue(v));
    if (typeof values === 'string') {
      const s = values.trim();
      if (!s) return [];
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) return parsed.map(v => this.coerceCellValue(v));
      } catch {
        /* not JSON — fall back to comma-separated tokens below */
      }
      return s.split(',').map(tok => this.coerceCellValue(tok));
    }
    return [];
  }

  private normalizeRows(rows: unknown): Array<Row> {
    const arr = Array.isArray(rows) ? rows : [];
    return arr.map((r: any) => ({ feature: r?.feature ?? '', values: this.normalizeValues(r?.values) }));
  }

  /**
   * Normalize and prune price rows. Clearing a price row in the builder can leave a
   * blank entry behind (empty feature and/or empty value objects); such rows still
   * rendered an empty `<tr>` (and a stray "$"). Drop any price row that has neither a
   * plan label nor a populated price value so cleared rows stop showing after deploy.
   */
  private normalizePriceRows(rows: unknown): Array<PriceRow> {
    const arr = Array.isArray(rows) ? rows : [];
    return arr
      .map((r: any) => ({ feature: r?.feature ?? '', values: Array.isArray(r?.values) ? r.values : [] }))
      .filter(row => this.priceRowHasContent(row));
  }

  private priceRowHasContent(row: PriceRow): boolean {
    const hasFeature = typeof row.feature === 'string' && row.feature.trim() !== '';
    const hasValue = row.values.some(v => {
      if (!v || typeof v !== 'object') return false;
      const amount = v.amount != null ? String(v.amount).trim() : '';
      return amount !== '' || !!v.link?.label;
    });
    return hasFeature || hasValue;
  }
  /**
   * The `<tr class="__thead-row">` element — it has overflow-x:auto in the SCSS
   * and is scrolled programmatically to mirror the tbody scroll position.
   */
  private theadRowRef?: HTMLElement;
  /**
   * The `<tbody class="__tbody">` element — the user-facing horizontal scroll container.
   */
  private tbodyRef?: HTMLElement;

  componentDidLoad() {
    if (!Build.isBrowser) return;

    const sentinel = this.el.querySelector<HTMLElement>(`.${COMPONENT_NAME}__sticky-sentinel`);
    if (sentinel) {
      this.sentinelObserver = new IntersectionObserver(
        ([entry]) => {
          this.isHeaderStuck = !entry.isIntersecting;
        },
        { threshold: 0 },
      );
      this.sentinelObserver.observe(sentinel);
    }

    // Keep <thead-row> and <tbody> in scroll sync — both have overflow-x:auto
    // via the existing SCSS. Listening on both allows scrolling from either side.
    this.tbodyRef?.addEventListener('scroll', this.syncFromBody, { passive: true });
    this.theadRowRef?.addEventListener('scroll', this.syncFromHead, { passive: true });
  }

  disconnectedCallback() {
    this.sentinelObserver?.disconnect();
    this.tbodyRef?.removeEventListener('scroll', this.syncFromBody);
    this.theadRowRef?.removeEventListener('scroll', this.syncFromHead);
  }

  /** tbody scrolled → mirror onto thead-row */
  private syncFromBody = () => {
    if (this.theadRowRef && this.tbodyRef) {
      this.theadRowRef.scrollLeft = this.tbodyRef.scrollLeft;
    }
  };

  /** thead-row scrolled → mirror onto tbody */
  private syncFromHead = () => {
    if (this.theadRowRef && this.tbodyRef) {
      this.tbodyRef.scrollLeft = this.theadRowRef.scrollLeft;
    }
  };

  private toggle() {
    this.expanded = !this.expanded;
  }

  private renderCheckmark(highlighted?: boolean) {
    const circleFill = highlighted ? '#0182E8' : '#CEE1FF59';
    const pathStroke = highlighted ? 'white' : '#0182E8';
    return (
      <svg
        class={`${COMPONENT_NAME}__icon ${COMPONENT_NAME}__icon--check`}
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        aria-label="Available"
        role="img"
      >
        <circle cx="12" cy="12" r="12" fill={circleFill} />
        <path d="M17 8L9.5 15.5L6 12" stroke={pathStroke} stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    );
  }

  private renderXMark() {
    return (
      <svg
        class={`${COMPONENT_NAME}__icon ${COMPONENT_NAME}__icon--x`}
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        aria-label="Not available"
        role="img"
      >
        <circle cx="12" cy="12" r="12" fill="#FFE9EA" />
        <path d="M15.77 8.23L8.23 15.77" stroke="#E64544" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M15.77 15.77L8.23 8.23" stroke="#E64544" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    );
  }

  private isTooltipValue(value: CellValue): value is TooltipValue {
    return typeof value === 'object' && value !== null && typeof (value as TooltipValue).tooltip === 'string';
  }

  private renderTooltipCell(value: TooltipValue) {
    const { text, trigger, tooltip } = value;
    // Split the label around the first occurrence of `trigger` so the anchor can be bolded
    // in place (matches the reference markup, e.g. "Available on Enterprise ($30 per month)").
    let before = '';
    let anchor = text;
    let after = '';
    if (trigger && text.includes(trigger)) {
      const i = text.indexOf(trigger);
      before = text.slice(0, i);
      anchor = trigger;
      after = text.slice(i + trigger.length);
    }

    return (
      <span class={`${COMPONENT_NAME}__tooltip`}>
        <span class={`${COMPONENT_NAME}__cell-text ${COMPONENT_NAME}__tooltip-description`}>
          {before}
          <span class={`${COMPONENT_NAME}__tooltip-trigger`}>{anchor}</span>
          {after}
        </span>
        <span class={`${COMPONENT_NAME}__tooltip-text`} role="tooltip">
          {tooltip}
        </span>
      </span>
    );
  }

  private renderCellValue(value: CellValue, highlighted?: boolean) {
    if (typeof value === 'boolean') {
      return value ? this.renderCheckmark(highlighted) : this.renderXMark();
    }

    if (this.isTooltipValue(value)) {
      return this.renderTooltipCell(value);
    }

    return <span class={`${COMPONENT_NAME}__cell-text`}>{value}</span>;
  }

  private renderPriceCell(val: PriceValue | undefined) {
    if (!val) return null;
    return (
      <span class={`${COMPONENT_NAME}__price-wrap`}>
        <p class={`${COMPONENT_NAME}__price`}>
          <span class={`${COMPONENT_NAME}__price-currency`}>{val.currency ?? '$'}</span>
          <span class={`${COMPONENT_NAME}__price-amount`}>{val.amount}</span>
        </p>
        {val.period && <p class={`${COMPONENT_NAME}__price-period`}>{val.period}</p>}
        {val.link && (
          <a href={val.link.href} class={`${COMPONENT_NAME}__price-link`}>
            {val.link.label}
          </a>
        )}
      </span>
    );
  }

  render() {
    const cols = (Array.isArray(this.columns) ? this.columns : []) as Array<Column>;
    const allRows = (Array.isArray(this.rows) ? this.rows : []) as Array<Row>;

    const hasToggle = this.initialVisibleRows > 0 && allRows.length > this.initialVisibleRows;
    const visibleRows = hasToggle && !this.expanded ? allRows.slice(0, this.initialVisibleRows) : allRows;
    const parsedPriceRows = (Array.isArray(this.priceRows) ? this.priceRows : []) as Array<PriceRow>;
    const hasPriceRows = parsedPriceRows.length > 0;
    const priceAtTop = this.priceRowsPosition === 'top';
    const lastPriceRowIndex = parsedPriceRows.length - 1;
    // The highlighted column's bottom border-radius goes on whichever group is rendered last.
    const featureRowIsLast = !hasPriceRows || priceAtTop;
    const priceRowIsLast = hasPriceRows && !priceAtTop;

    const wrapperClass = [
      `${COMPONENT_NAME}__table-wrapper`,
      this.isHeaderStuck && `${COMPONENT_NAME}__table-wrapper--sticky-active`,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <me-section class={COMPONENT_NAME} yGap="4xl" yGapLg="5xl">
        {/* Sentinel observed by IntersectionObserver — when it leaves the viewport
            the sticky header drop-shadow class is toggled on the wrapper. */}
        <div class={`${COMPONENT_NAME}__sticky-sentinel`} aria-hidden="true" />

        <div class={`${COMPONENT_NAME}__intro`}>
          <slot name="heading" />
          <slot name="subtitle" />
        </div>

        <div class={wrapperClass}>
          <table class={`${COMPONENT_NAME}__table`}>
            <thead class={`${COMPONENT_NAME}__thead`}>
              {/* ref wired here — this <tr> has overflow-x:auto and is the header scroll container */}
              <tr ref={(el: HTMLElement) => (this.theadRowRef = el)} class={`${COMPONENT_NAME}__thead-row`}>
                <th
                  class={`${COMPONENT_NAME}__column ${COMPONENT_NAME}__column--dummu ${COMPONENT_NAME}__thead-column--dummu ${COMPONENT_NAME}__thead-column--disclaimer`}
                  scope="col"
                >
                  <slot name="disclaimer" />
                </th>
                {cols.map(col => (
                  <th
                    class={[
                      `${COMPONENT_NAME}__column ${COMPONENT_NAME}__thead-column`,
                      col.highlighted ? `${COMPONENT_NAME}__column--highlighted ${COMPONENT_NAME}__thead-column--highlighted` : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    scope="col"
                  >
                    {col.logoSrc ? (
                      <img src={col.logoSrc} alt={col.label} class={`${COMPONENT_NAME}__col-logo`} loading="lazy" width="120" height="32" />
                    ) : (
                      <span class={`${COMPONENT_NAME}__col-label`}>{col.label}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            {/* ref wired here — this <tbody> has overflow-x:auto and is the body scroll container */}
            <tbody ref={(el: HTMLElement) => (this.tbodyRef = el)} class={`${COMPONENT_NAME}__tbody`}>
              {priceAtTop && parsedPriceRows.map((row) => (
                <tr class={`${COMPONENT_NAME}__row ${COMPONENT_NAME}__row--price`}>
                  <th class={`${COMPONENT_NAME}__column body-column body-column--feature ${COMPONENT_NAME}__column--feature`} scope="row">
                    {row.feature}
                  </th>
                  {cols.map((col, idx) => (
                    <td
                      class={[
                        `${COMPONENT_NAME}__column body-column`,
                        col.highlighted ? `${COMPONENT_NAME}__column--highlighted` : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {this.renderPriceCell(row.values[idx])}
                    </td>
                  ))}
                </tr>
              ))}
              {visibleRows.map((row, index, array) => (
                <tr
                  class={[
                    `${COMPONENT_NAME}__row`,
                    // Collapsed ("short") state: the last visible row gets a bottom fade overlay
                    // that teases the hidden rows (matches live signnow.com). When bottom price
                    // rows are present they render last, so the fade goes there instead (see below).
                    hasToggle && !this.expanded && featureRowIsLast && index === array.length - 1 && `${COMPONENT_NAME}__row--fade`,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <th class={`${COMPONENT_NAME}__column body-column body-column--feature ${COMPONENT_NAME}__column--feature`} scope="row">
                    {row.feature}
                  </th>
                  {cols.map((col, idx) => (
                    <td
                      class={[
                        `${COMPONENT_NAME}__column body-column`,
                        col.highlighted ? `${COMPONENT_NAME}__column--highlighted` : '',
                        col.highlighted && featureRowIsLast && index === array.length - 1 && `${COMPONENT_NAME}__column--highlighted--last`,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {row.values[idx] !== undefined ? this.renderCellValue(row.values[idx], col.highlighted) : null}
                    </td>
                  ))}
                </tr>
              ))}
              {!priceAtTop && parsedPriceRows.map((row, index) => (
                <tr
                  class={[
                    `${COMPONENT_NAME}__row ${COMPONENT_NAME}__row--price`,
                    // Bottom price rows render last, so in the collapsed state the fade overlay
                    // belongs on the final price row rather than the last feature row.
                    hasToggle && !this.expanded && index === lastPriceRowIndex && `${COMPONENT_NAME}__row--fade`,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <th class={`${COMPONENT_NAME}__column body-column body-column--feature ${COMPONENT_NAME}__column--feature`} scope="row">
                    {row.feature}
                  </th>
                  {cols.map((col, idx) => (
                    <td
                      class={[
                        `${COMPONENT_NAME}__column body-column`,
                        col.highlighted ? `${COMPONENT_NAME}__column--highlighted` : '',
                        col.highlighted && priceRowIsLast && index === lastPriceRowIndex && `${COMPONENT_NAME}__column--highlighted--last`,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {this.renderPriceCell(row.values[idx])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {hasToggle && (
          <div class={`${COMPONENT_NAME}__show-more`}>
            {this.showMoreTeaser && <span class={`${COMPONENT_NAME}__show-more-teaser`}>{this.showMoreTeaser}</span>}
            <button class={`${COMPONENT_NAME}__toggle`} type="button" onClick={() => this.toggle()} aria-expanded={String(this.expanded)}>
              {this.expanded ? this.showLessLabel : this.showMoreLabel}
            </button>
          </div>
        )}
      </me-section>
    );
  }
}
